import React from "react";
import { Col, FormGroup, Input, Label, Row } from "reactstrap";
import { LabelWithToolTip } from "../../../atoms/LabelWithToolTip";
import Select from "react-select";

const NonIBANForm = (props) => {
  const bicField = props.register("bic", { required: true });
  const corrBicField = props.register("correspondentBic", { required: false });

  const field = (label, value) => (
    <div className="vbdm__field">
      <div className="vbdm__field-label">{label}</div>
      <div className={`vbdm__field-value${!value ? ' vbdm__field-value--empty' : ''}`}>
        {value || '\u2014'}
      </div>
    </div>
  );

  // // this is used for checking if USD is selected to conditionally render the ABA Routing Number field
  // const selectedCurrencyId = props.currencyList?.find((c) => c.name === props.currency)?.id || "";
  // const selectedCurrency = props.currencyList?.find((c) => c.id == selectedCurrencyId);
  // const isUSDSelected = selectedCurrency?.name === "USD";

  return (
    <>

      {/* Currency and Keep This Only fields */}
      <Row>
        <Col sm="12" md="6">
          <FormGroup>
            <LabelWithToolTip
              id="labelCurrency"
              labelText="Currency *"
              tooltipText="Select the currency for this bank account"
            />
            {(() => {
              const opts = (props.sortedCurrencyList || []).map(o => ({ value: o.id, label: o.name }));
              const currentId = props.currencyList?.find(c => c.name === props.currency)?.id;
              return (
                <>
                  <input type="hidden" {...props.register("currencyId", { required: true })} />
                  <Select
                    options={opts}
                    value={opts.find(o => String(o.value) === String(currentId)) || null}
                    onChange={(selected) => {
                      const id = selected?.value ?? "";
                      props.setValue("currencyId", id);
                      props.onCurrencyChanged({ target: { value: id } });
                    }}
                    placeholder="Search currency…"
                    isClearable={false}
                  />
                </>
              );
            })()}
          </FormGroup>
        </Col>
      </Row>


      {/* Account Number Field */}
      <Row>
        <Col sm="12">
          <FormGroup>
            <LabelWithToolTip
              id="labelAccountNumber"
              labelText="Account Number *"
              tooltipText="Bank account number as it appears on your bank statement. Maximum 34 characters. Only alphanumeric characters allowed. Spaces and hyphens (-) are not permitted."
            />
            <input
              type="text"
              {...props.register("bankAccountNumber", { required: true })}
              value={props.bankAccountNumber}
              onChange={props.handleAccountNo}
              maxLength={34}
              className="form-control"
            />
          </FormGroup>
        </Col>
      </Row>

      {/* Beneficiary Name Field */}
      <Row>
        <Col sm="12">
          <FormGroup>
            <LabelWithToolTip
              id="labelBeneficiaryName"
              labelText="Beneficiary Name *"
              tooltipText="Name of the account holder as it appears on the bank account. 
              Only alphanumeric characters, spaces, parentheses, commas and periods. Ampersands (&) are not permitted."
            />
            <input
              type="text"
              {...props.register("beneficiaryBankAccountName", { required: true })}
              value={props.beneficiaryName}
              onChange={props.onBeneficiaryNameChange}
              maxLength="100"
              className="form-control"
            />
            
          </FormGroup>
        </Col>
      </Row>


      {/* SWIFT/BIC Field */}
      <Row>
        <Col sm="12">
          <FormGroup>
            <LabelWithToolTip
              id="labelSwift"
              labelText="Beneficiary Bank SWIFT BIC *"
              tooltipText="Bank Identifier Code (BIC/SWIFT). Must be 8 or 11 characters. Only alphanumeric characters allowed (e.g., BUKBGB22)."
            />
            <input
              type="text"
              {...bicField}
              onChange={(e) => {
                bicField.onChange(e);
                if (props.resetMetadata) props.resetMetadata();
              }}
              className="form-control"
            />
            
          </FormGroup>
        </Col>
      </Row>


      {/* Correspondent BIC SWIFT Field */}
      <Row>
        <Col sm="12">
          <FormGroup>
            <LabelWithToolTip
              id="labelCorrespondentBic"
              labelText="Correspondent Bank SWIFT BIC"
              tooltipText="Intermediary bank SWIFT code if payments need to be routed through another bank (optional). 
              Must be 8 or 11 characters if provided. Only alphanumeric characters allowed."
            />
            <input
              type="text"
              {...corrBicField}
              onChange={(e) => {
                corrBicField.onChange(e);
                if (props.resetMetadata) props.resetMetadata();
              }}
              className="form-control"
            />
          </FormGroup>
        </Col>
      </Row>

      {/* SWIFT Validation Results - shown after successful validation */}
      {props.isBicValidated && (
        <div className="swift-results">

          {/* Beneficiary Bank Section */}
          <div className="swift-results__section">
            <div className="swift-results__fields">
              {/* Hidden inputs keep form state; display uses vbdm__field cards */}
              <input type="hidden" {...props.register("bankName")} />
              <input type="hidden" {...props.register("branch")} />
              <input type="hidden" {...props.register("bankAddress")} />
              <input type="hidden" {...props.register("bankCity")} />
              <input type="hidden" {...props.register("bankZip")} />
              <input type="hidden" {...props.register("isoCountryCode")} />
              <input type="hidden" {...props.register("bankCountry")} />
              <div className="vbdm__grid">
                {field("Beneficiary Bank SWIFT", props.validatedBicCode)}
                {field("Beneficiary Bank Name", props.getValues("bankName"))}
                {field("Beneficiary Bank Address", props.getValues("bankAddress"))}
                {field("Beneficiary Bank City", props.getValues("bankCity"))}
                {field("Beneficiary Bank Postal Code", props.getValues("bankZip"))}
                {field("Beneficiary Bank Country Name", props.getValues("bankCountry"))}
                {field("Beneficiary Bank Country Code", props.isoCountryCode)}
              </div>
            </div>
          </div>


          {/* Correspondent Bank Section - shown only when a correspondent BIC was provided and validated */}
          {props.isCorrespondentBicValidated && props.correspondentBicData && (
            <>
            <div className="vbdm__divider" />
            <div className="swift-results__section swift-results__section--corresponding">
              <div className="swift-results__fields">
                <div className="vbdm__grid">
                  {field("Correspondent Bank SWIFT", props.validatedCorrespondentBicCode)}
                  {field("Correspondent Bank Name", props.correspondentBicData.institutionName)}
                  {field("Correspondent Bank Address", props.correspondentBicData.opAddress)}
                  {field("Correspondent Bank City", props.correspondentBicData.opCity)}
                  {field("Correspondent Bank Postal Code", props.correspondentBicData.opZipCode)}
                  {field("Correspondent Bank Country Name", props.correspondentBicData.countryName)}
                  {field("Correspondent Bank Country Code", props.correspondentBicData.countryIso || props.correspondentBicData.isoCountryCode || props.correspondentBicData.countryCode)}
                </div>
              </div>
            </div>
            </>
          )}
 
        </div>
      )}

    </>
  );
};

export default NonIBANForm;
